/**
 * Вкладка «История» окна товара: лента событий по артикулу
 * (GET /products/:article/history) на общем компоненте ActivityTimeline.
 */

import React, { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { ActivityTimeline } from '../../../components/ui/ActivityTimeline'
import type { ActivityEvent, ActivityEventType } from '../../../components/ui/ActivityTimeline'
import { productsV2API } from '../api'
import { useEntityNavigator, type EntityTarget } from '../../../components/entity-view/navigatorContext'
import type { ProductHistoryItem, ProductHistoryType } from '../types'

/** Тип события ленты ПИМ → значок общей ленты. */
const TIMELINE_TYPE: Record<ProductHistoryType, ActivityEventType> = {
  product: 'description',
  card: 'moderation',
  send: 'sync',
  visual: 'media',
  pipeline: 'other',
  activity: 'other',
}

export function historyEntityTarget(item: ProductHistoryItem): EntityTarget | null {
  const cardId = Number(item.entity?.key)
  if (item.entity?.kind === 'card' && Number.isInteger(cardId) && cardId > 0
    && item.seller_id && (item.marketplace === 'wb' || item.marketplace === 'ozon' || item.marketplace === 'ym')) {
    return { kind: 'draft', cardId, sellerId: item.seller_id, marketplace: item.marketplace }
  }
  return null
}

export function historyToEvents(items: ProductHistoryItem[], open?: (target: EntityTarget) => void): ActivityEvent[] {
  return items.map((item) => {
    const target = historyEntityTarget(item)
    const context = item.entity?.label || [item.marketplace?.toUpperCase(), item.seller_id ? `Кабинет ${item.seller_id}` : undefined].filter(Boolean).join(' · ')
    return {
      id: item.id,
      at: item.at,
      type: TIMELINE_TYPE[item.type] ?? 'other',
      title: item.title,
      description: item.description,
      actor: item.actor?.name,
      ...(context ? { body: target && open
        ? <button type="button" className="text-blue-600 hover:underline dark:text-blue-400" onClick={() => open(target)}>Открыть карточку · {context}</button>
        : <span>{context}</span> } : {}),
    }
  })
}

export interface HistoryData {
  items: ProductHistoryItem[] | null
  error: string | null
}

/** Загрузка ленты событий по артикулу; ошибка и «ещё не пришло» различаются. */
export function useProductHistory(article: string | null | undefined): HistoryData {
  const [state, setState] = useState<HistoryData>({ items: null, error: null })

  useEffect(() => {
    if (!article) {
      setState({ items: null, error: null })
      return undefined
    }
    let cancelled = false
    setState({ items: null, error: null })
    Promise.resolve()
      .then(() => productsV2API.getHistory(article))
      .then((data) => {
        if (!cancelled) setState({ items: Array.isArray(data?.items) ? data.items : [], error: null })
      })
      .catch((e: any) => {
        if (cancelled) return
        setState({
          items: null,
          error: e?.response?.data?.error?.message || e?.message || 'Не удалось загрузить историю',
        })
      })
    return () => {
      cancelled = true
    }
  }, [article])

  return state
}

export const ProductHistoryPanel: React.FC<{ article: string; data?: HistoryData }> = ({ article, data }) => {
  const navigator = useEntityNavigator()
  const own = useProductHistory(data ? null : article)
  const { items, error } = data ?? own

  const events = useMemo(() => historyToEvents(items ?? [], navigator.available ? navigator.open : undefined), [items, navigator.available, navigator.open])

  if (error) return <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
  if (items === null) {
    return (
      <p className="flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-400">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Загрузка истории
      </p>
    )
  }
  return <ActivityTimeline events={events} emptyState="По этому товару событий пока нет" />
}
